import { snakeCase } from "../../utils";
import type { EventDefinition, LinkType, ParamsType } from "../types";

// QCP page events
export const qcpEventDefinitions: EventDefinition[] = [
  {
    // QDP details button
    name: "detailsClicked",
    type: "structured",
    makePayload: () => ({
      category: "comparison_cta",
      action: "link_click",
      label: "Details",
    }),
  },
  {
    // Buy button
    name: "selectClicked",
    type: "structured",
    makePayload: () => ({
      category: "comparison_cta",
      action: "link_click",
      label: "Buy",
    }),
  },
  {
    // Quote Details Slider Next steps button
    name: "nextStepsClicked",
    type: "structured",
    makePayload: () => ({
      category: "quote_details_slider_next_step_cta",
      action: "link_click",
      label: "Next steps",
    }),
  },
  {
    // Toggle deductibles accordion
    name: "deductiblesClicked",
    type: "structured",
    makePayload: () => ({
      category: "us-qcp-react",
      action: "view_deductables_clicked",
      label: "view_deductables_clicked",
      property: window.location.href,
    }),
  },
  {
    name: "deductiblesClickedUk",
    type: "structured",
    makePayload: params => {
      const { label, deviceType } = params as ParamsType;
      const urlFriendlyLabel = label.replace(/ /g, "-").toLowerCase();

      return {
        category: `uk-qcp-react-${deviceType}-${urlFriendlyLabel}-view-excess-toggle`,
        action: "view_excess_clicked",
        label,
        property: window.location.href,
      };
    },
  },
  {
    // Quote Details Slider opened
    name: "sliderOpened",
    type: "structured",
    makePayload: params => {
      const { label } = params as ParamsType;

      return {
        category: "comparison_cta",
        action: "quote_details_slider_opened",
        label,
      };
    },
  },
  {
    // Coverage modal opened
    name: "coverageModalOpened",
    type: "structured",
    makePayload: params => {
      const {
        category,
        product = "extra_coverage",
        title,
      } = params as ParamsType;
      const productLabel = snakeCase(product);

      return {
        category,
        action: `${productLabel}_${snakeCase(title)}_popup_opened`,
        label: productLabel,
        property: window.location.href,
      };
    },
  },
  {
    // Toggle cover select
    name: "coverChanged",
    type: "unstructured",
    makePayload: params => {
      const { name = "", fromValue = "", toValue = "" } = params as ParamsType;
      // Derive data
      let action = "change";

      // This logic is taken directly from Chopin without documentation:
      // If a cover has zero value, then change the action to add or remove
      if (fromValue === "0") {
        action = "add";
      }
      if (toValue === "0") {
        action = "remove";
      }

      return {
        schema:
          "iglu:com.simplybusiness/comparison_page_cover_changed/jsonschema/1-0-0",
        data: {
          name,
          action,
          from_value: fromValue,
          to_value: toValue,
        },
      };
    },
    contexts: ["distributionChannelContext"],
  },
  {
    name: "ratingsModalOpened",
    type: "structured",
    makePayload: params => {
      const { category, label } = params as ParamsType;

      return {
        category,
        action: "insurer_rating_help_popup_triggered",
        label,
        property: window.location.href,
      };
    },
  },
  {
    name: "coverToggleOpened",
    type: "structured",
    makePayload: () => ({
      category: "qcp_limit_interaction",
      label: "limit_interaction",
      action: "limit_interaction_clicked",
      property: window.location.href,
    }),
  },
  {
    name: "paymentToggleClicked",
    type: "structured",
    makePayload: params => {
      const { category, label } = params as ParamsType;

      return {
        category,
        action: "button_click",
        label,
        property: window.location.href,
      };
    },
  },
  {
    name: "insurerDetailsAccordionClicked",
    type: "structured",
    makePayload: () => ({
      category: "quote_details_slider_insurer_details_description",
      action: "accordion_clicked",
      label: "accordion_clicked",
      property: window.location.href,
    }),
  },
  {
    name: "priceDetailsPopUpOpened",
    type: "structured",
    makePayload: () => ({
      category: "price_details",
      action: "price_details_popup_opened",
      label: "Price details",
      property: window.location.href,
    }),
  },
  {
    name: "editLimitButtonClicked",
    type: "structured",
    makePayload: () => ({
      category: "cover_limit_changes",
      action: "edit_limit_button_clicked",
      label: "Edit Limit",
      property: window.location.href,
    }),
  },
  {
    name: "applyButtonClicked",
    type: "structured",
    makePayload: () => ({
      category: "cover_limit_changes",
      action: "apply_button_clicked",
      label: "Apply Button Clicked",
      property: window.location.href,
    }),
  },
  {
    name: "coverageInfoClicked",
    type: "structured",
    makePayload: params => {
      const { deviceType, open = true } = params as ParamsType;

      return {
        action: "show_coverage_info_clicked",
        category: `uk-qcp-react-${deviceType}-show-coverage-info-toggle`,
        label: `${open ? "show" : "hide"}_coverage_info_clicked`,
        property: window.location.href,
      };
    },
  },
  {
    name: "backToApplication",
    type: "unstructured",
    makePayload: params => {
      const { journeyId, vertical } = params as ParamsType;

      return {
        schema:
          "iglu:com.simplybusiness/qcp_back_to_application/jsonschema/1-0-0",
        data: {
          journey_id: journeyId,
          vertical,
        },
      };
    },
    contexts: ["serviceChannelContext"],
  },
  // No quotes referral
  {
    name: "coverOptionsLinkClicked",
    type: "structured",
    makePayload: () => ({
      category: "uk-qcp-react-cover-options-link",
      action: "cover_options_link_clicked",
      label: "cover_options_link_clicked",
      property: window.location.href,
    }),
  },
  {
    name: "referralReasonsVariantBCoverOptionsLinkClicked",
    type: "structured",
    makePayload: () => ({
      category: "uk-24-12-qcp-mvt-referral-reasons",
      action: "cover_options_link_clicked_variant_b",
      label: "cover_options_link_clicked_variant_b",
      property: window.location.href,
    }),
  },
  {
    name: "referralReasonsVariantCCoverOptionsLinkClicked",
    type: "structured",
    makePayload: () => ({
      category: "uk-24-12-qcp-mvt-referral-reasons",
      action: "cover_options_link_clicked_variant_c",
      label: "cover_options_link_clicked_variant_c",
      property: window.location.href,
    }),
  },
  {
    name: "phoneNumberLinkClicked",
    type: "structured",
    makePayload: () => ({
      category: "uk-qcp-react-phone-number-link",
      action: "phone_number_link_clicked",
      label: "phone_number_link_clicked",
      property: window.location.href,
    }),
  },
  {
    name: "linkClicked",
    type: "unstructured",
    makePayload: params => {
      const { targetUrl, elementContent } = params as LinkType;

      return {
        schema:
          "iglu:com.snowplowanalytics.snowplow/link_click/jsonschema/1-0-1",
        data: {
          targetUrl,
          elementContent,
        },
      };
    },
  },
  {
    name: "qcpUpsellAccordionClicked",
    type: "structured",
    makePayload: params => ({
      category: "us-qcp-upsells",
      action: "accordion_clicked_qcp",
      label: params?.label as string,
      property: window.location.href,
    }),
  },
  {
    name: "qdsUpsellAccordionClicked",
    type: "structured",
    makePayload: params => ({
      category: "us-qcp-upsells",
      action: "accordion_clicked_slider",
      label: params?.label as string,
      property: window.location.href,
    }),
  },
  {
    name: "qcpUpsellTooltipHelperClicked",
    type: "structured",
    makePayload: params => ({
      category: "us-qcp-upsells",
      action: "tooltip_helper_clicked_qcp",
      label: params?.label as string,
      property: window.location.href,
    }),
  },
  {
    name: "qdsUpsellTooltipHelperClicked",
    type: "structured",
    makePayload: params => ({
      category: "us-qcp-upsells",
      action: "tooltip_helper_clicked_slider",
      label: params?.label as string,
      property: window.location.href,
    }),
  },
  {
    name: "qcpQuoteButtonClicked",
    type: "structured",
    makePayload: payload => ({
      category: "quote-buttons",
      action: "button-clicked",
      label: payload?.label as string,
      property: window.location.href,
    }),
  },
  {
    name: "layoutToggleClickedExperiment",
    type: "structured",
    makePayload: params => {
      const { layout } = params as ParamsType;

      return {
        category: "uk-25-04-16-gridview_layout",
        action: "layout_toggle_clicked",
        label: layout,
        property: window.location.href,
      };
    },
  },
  {
    name: "selectCoverOptionsClicked",
    type: "structured",
    makePayload: payload => {
      return {
        category: "uk-25-09-qcp-covers-demand-test",
        action: "cover_options_clicked",
        label: payload?.label as string,
        property: window.location.href,
      };
    },
  },
];
